/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { 
  Box, 
  Chip, 
  CircularProgress,  
  IconButton,  
  Menu,  
  MenuItem,  
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Tooltip, 
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import useCurrencyService from '../../../../../components/hooks/useCurrencyService';
import useSelectedCompany from '../../../../../components/hooks/useSelectedCompany';
import TableTimeStamps from '../../../../../components/utils/TableTimeStamps';
import DateFormatter from '../../../../../components/utils/DateFormatter';
import NameAvatar from '../../../../../components/avatars/Avatar';
import Copyable from '../../../../../components/utils/Copyable';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';


import { 
  GetBillingAddonsBIZcore,
  GetBillingCrossBorderPaymentBIZcore,
  GetBillingDomesticWalletDepositBIZcore,
  GetBillingDomesticWalletWithdrawalBIZcore,
  GetBillingPaysBIZcore,
  GetBillingPlanBIZcore, 
  GetBillingReceivesBIZcore
} from '../../../../../redux/actions/business/core/companyBillingActions';

import global from "../../../../../global.scss";

const BillingPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core"]);
  const { handleShowAmount } = useCurrencyService();
  const companyId = useSelectedCompany();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [statusFilter, setStatusFilter] = useState("PLAN");

  const [billingList, setBillingList] = useState([]);
  const [billingTotal, setBillingTotal] = useState(0);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsListPLAN = [
    {
      label: "Transaction Id",
      sort: "transaction_id",
    },
    {
      label: "Billing date",
      sort: "billing_date",
    },
    {
      label: "Settlement date",
      sort: "settlement_date",
    },
    {
      label: "Subscription plan",
      sort: null,
    },
    {
      label: "Period billed",
      sort: null,
    },
    {
      label: "Fees",
      sort: "fee_billed",
      align: "right"
    },
    {
      label: "Status",
      sort: "fee_status",
      align: "right"
    },
  ];

  const tableHeadingsListADDONS = [];

  const tableHeadingsListDOM_PAY = [
    {
      label: "Transaction Id",
      sort: "transaction_id",
    },
    {
      label: "Billing date",
      sort: "billing_date",
    },
    {
      label: "Debit date",
      sort: null,
    },
    {
      label: "Settlement date",
      sort: "settlement_date",
    },
    {
      label: "Vendor",
      sort: "ref_payment_id",
    },
    {
      label: "Payment amount",
      sort: null,
      align: "right"
    },
    {
      label: "Fees",
      sort: "fee_billed",
      align: "right"
    },
    {
      label: "Status",
      sort: "fee_status",
      align: "right"
    },
  ];

  const tableHeadingsListDOM_RCV = [
    {
      label: "Transaction Id",
      sort: "transaction_id",
    },
    {
      label: "Billing date",
      sort: "billing_date",
    },
    {
      label: "Settlement date",
      sort: "settlement_date",
    },
    {
      label: "Customer",
      sort: "ref_receipt_id",
    },
    {
      label: "Invoice number",
      sort: "ref_receipt_id",
    },
    {
      label: "Received amount",
      sort: null,
      align: "right"
    },
    {
      label: "Fees",
      sort: "fee_billed",
      align: "right"
    },
    {
      label: "Status",
      sort: "fee_status",
      align: "right"
    },
  ];

  const tableHeadingsListDOM_WALLET_DEPOSIT = [
    {
      label: "Transaction Id",
      sort: "transaction_id",
    },
    {
      label: "Billing date",
      sort: "billing_date",
    },
    {
      label: "Settlement date",
      sort: "settlement_date",
    },
    {
      label: "Comment",
      sort: null,
    },
    {
      label: "Fees",
      sort: "fee_billed",
      align: "right"
    },
    {
      label: "Status",
      sort: "fee_status",
      align: "right"
    },
  ];

  const tableHeadingsListDOM_WALLET_WITHDRAWAL = [
    {
      label: "Transaction Id",
      sort: "transaction_id",
    },
    {
      label: "Billing date",
      sort: "billing_date",
    },
    {
      label: "Settlement date",
      sort: "settlement_date",
    },
    {
      label: "Comment",
      sort: null,
    },
    {
      label: "Fees",
      sort: "fee_billed",
      align: "right"
    },
    {
      label: "Status",
      sort: "fee_status",
      align: "right"
    },
  ];

  const tableHeadingsListINT_PAY = [
    {
      label: "Transaction Id",
      sort: "transaction_id",
    },
    {
      label: "Billing date",
      sort: "billing_date",
    },
    {
      label: "Debit date",
      sort: null,
    },
    {
      label: "Settlement date",
      sort: "settlement_date",
    },
    {
      label: "Vendor",
      sort: "ref_payment_id",
    },
    {
      label: "Payment amount",
      sort: null,
      align: "right"
    },
    {
      label: "Fees",
      sort: "fee_billed",
      align: "right"
    },
    {
      label: "Status",
      sort: "fee_status",
      align: "right"
    },
  ];

  const tableHeadingsListINT_RCV = [
    {
      label: "Transaction Id",
      sort: "transaction_id",
    },
    {
      label: "Billing date",
      sort: "billing_date",
    },
    {
      label: "Settlement date",
      sort: "settlement_date",
    },
    {
      label: "Customer",
      sort: "ref_receipt_id",
    },
    {
      label: "Invoice number",
      sort: "ref_receipt_id",
    },
    {
      label: "Received amount",
      sort: null,
      align: "right"
    },
    {
      label: "Fees",
      sort: "fee_billed",
      align: "right"
    },
    {
      label: "Status",
      sort: "fee_status",
      align: "right"
    },
  ];

  const tableHeadingsListINT_WALLET = [
    {
      label: "Transaction Id",
      sort: "transaction_id",
    },
    {
      label: "Billing date",
      sort: "billing_date",
    },
    {
      label: "Settlement date",
      sort: "settlement_date",
    },
    {
      label: "Received amount",
      sort: null,
      align: "right"
    },
    {
      label: "Fees",
      sort: "fee_billed",
      align: "right"
    },
    {
      label: "Status",
      sort: "fee_status",
      align: "right"
    },
  ];



  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetBillingList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection, statusFilter]);

  useEffect(() => {
    try {
      if(statusFilter === "PLAN"){
        formatBillingList(state.billingPlansList.records || []);
        setBillingTotal(state.billingPlansList.total || 0);
      } else if (statusFilter === "ADDONS"){ 
        formatBillingList(state.billingAddonsList.records || []);
        setBillingTotal(state.billingAddonsList.total || 0);
      } else if (statusFilter === "DOM_PAY"){
        formatBillingList(state.billingPaysList.records || []);
        setBillingTotal(state.billingPaysList.total || 0);
      } else if (statusFilter === "DOM_RCV"){
        formatBillingList(state.billingReceivesList.records || []);
        setBillingTotal(state.billingReceivesList.total || 0);
      } else if (statusFilter === "DOM_WALLET_DEPOSIT"){
        formatBillingList(state.billingDomesticWalletDeopositsList.records || []);
        setBillingTotal(state.billingDomesticWalletDeopositsList.total || 0);
      } else if (statusFilter === "DOM_WALLET_WITHDRAWAL"){
        formatBillingList(state.billingDomesticWalletWithdrawalList.records || []);
        setBillingTotal(state.billingDomesticWalletWithdrawalList.total || 0);
      } else if (statusFilter === "INT_PAY"){
        formatBillingList(state.billingCBPList.records || []);
        setBillingTotal(state.billingCBPList.total || 0);
      } else if (statusFilter === "INT_RCV"){
        formatBillingList(state.billingCBPList.records || []);
        setBillingTotal(state.billingCBPList.total || 0);
      } else if (statusFilter === "INT_WALLET"){
        formatBillingList(state.billingCBPList.records || []);
        setBillingTotal(state.billingCBPList.total || 0);
      } else {}
     
    } catch (err) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const formatBillingList = (list) => {
    setBillingList(list)
  };

  async function handleGetBillingList(customLoading) {
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;

    if(statusFilter === "PLAN"){
      await dispatch(GetBillingPlanBIZcore(companyId, page+1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
    } else if (statusFilter === "ADDONS"){ 
      await dispatch(GetBillingAddonsBIZcore(companyId, page+1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
    } else if (statusFilter === "DOM_PAY"){
      await dispatch(GetBillingPaysBIZcore(companyId, page+1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
    } else if (statusFilter === "DOM_RCV"){
      await dispatch(GetBillingReceivesBIZcore(companyId, page+1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
    } else if (statusFilter === "DOM_WALLET_DEPOSIT"){
      await dispatch(GetBillingDomesticWalletDepositBIZcore(companyId, page+1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
    } else if (statusFilter === "DOM_WALLET_WITHDRAWAL"){
      await dispatch(GetBillingDomesticWalletWithdrawalBIZcore(companyId, page+1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
    } else if (statusFilter === "INT_PAY"){
      query += `&type=Pay`;
      await dispatch(GetBillingCrossBorderPaymentBIZcore(companyId, page+1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
    } else if (statusFilter === "INT_RCV"){
      query += `&type=Get Paid`;
      await dispatch(GetBillingCrossBorderPaymentBIZcore(companyId, page+1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
    } else if (statusFilter === "INT_WALLET"){
      query += `&type=Wallet`;
      await dispatch(GetBillingCrossBorderPaymentBIZcore(companyId, page+1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
    } else {}
  };

  const getStatusChipColor = (status) => {
    switch(status){
      case "Scheduled": return "info";
      case "Processing": return "info";
      case "Paid": return "success";
      case "Failed": return "error";

      default: return "default";
    }
  };

  return (
    <Box>

      <TopBar
        title={"Billing"}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
        />

      <Box 
        className="flexCenterSBRow" 
        mb={"15px"}>
        <Box  
          className="flexCenter_Row"
          gap="15px">
          <Chip
            variant='filled'
            color={statusFilter === "PLAN" ? 'success' : 'default'}
            label="Plan"
            onClick={() => {
              setStatusFilter("PLAN");
            }}
            />
          <Chip
            variant='filled'
            color={statusFilter === "ADDONS" ? 'success' : 'default'}
            label="Addons"
            onClick={() => {
              setStatusFilter("ADDONS");
            }}
            />
          <Tooltip
            title="Domestic">
            <Box
              className="flexCenter_Row"
              gap="10px"
              sx={{
                backgroundColor: "#6388cdff",
                borderRadius: "16px",
                padding: "10px"
              }}>
              <Chip
                variant='filled'
                color={statusFilter === "DOM_PAY" ? 'success' : 'default'}
                label="Pay"
                onClick={() => {
                  setStatusFilter("DOM_PAY");
                }}
                />
              <Chip 
                variant='filled'
                color={statusFilter === "DOM_RCV" ? 'info' : 'default'}
                label="Get Paid"
                onClick={() => {
                  setStatusFilter("DOM_RCV");
                }}
                />
              <Chip
                variant='filled'
                color={statusFilter === "DOM_WALLET_DEPOSIT" ? 'warning' : 'default'}
                label="Wallet Deposit"
                onClick={() => {
                  setStatusFilter("DOM_WALLET_DEPOSIT");
                }}
                />
              <Chip
                variant='filled'
                color={statusFilter === "DOM_WALLET_WITHDRAWAL" ? 'warning' : 'default'}
                label="Wallet Withdrawal"
                onClick={() => {
                  setStatusFilter("DOM_WALLET_WITHDRAWAL");
                }}
                />
            </Box>
          </Tooltip>
          <Tooltip
            title="International">
            <Box
              className="flexCenter_Row"
              gap="10px"
              sx={{
                backgroundColor: "#63cd93ff",
                borderRadius: "16px",
                padding: "10px"
              }}>
              <Chip
                variant='filled'
                color={statusFilter === "INT_PAY" ? 'success' : 'default'}
                label="Pay"
                onClick={() => {
                  setStatusFilter("INT_PAY");
                }}
                />
              <Chip 
                variant='filled'
                color={statusFilter === "INT_RCV" ? 'info' : 'default'}
                label="Get Paid"
                onClick={() => {
                  setStatusFilter("INT_RCV");
                }}
                />
              <Chip
                variant='filled'
                color={statusFilter === "INT_WALLET" ? 'warning' : 'default'}
                label="Wallet"
                onClick={() => {
                  setStatusFilter("INT_WALLET");
                }}
                />
            </Box>
          </Tooltip>
          
        </Box>
      </Box>

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          billingList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No billing found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  (statusFilter === "PLAN" 
                    ? tableHeadingsListPLAN
                    : statusFilter === "ADDONS"
                      ? tableHeadingsListADDONS
                      : statusFilter === "DOM_PAY"
                        ? tableHeadingsListDOM_PAY
                        : statusFilter === "DOM_RCV"
                          ? tableHeadingsListDOM_RCV
                          : statusFilter === "DOM_WALLET_DEPOSIT"
                            ? tableHeadingsListDOM_WALLET_DEPOSIT
                            : statusFilter === "DOM_WALLET_WITHDRAWAL"
                              ? tableHeadingsListDOM_WALLET_WITHDRAWAL
                              : statusFilter === "INT_PAY"  
                                ? tableHeadingsListINT_PAY
                                : statusFilter === "INT_RCV"
                                  ? tableHeadingsListINT_RCV
                                  : statusFilter === "INT_WALLET"
                                    ? tableHeadingsListINT_WALLET
                                    : [])
                  .map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                billingList.map((row, index) => (
                  statusFilter === "PLAN"
                  ?
                  (<TableRow 
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='body2'>
                        {row.transaction_id || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.billing_date ? DateFormatter(row.billing_date) : <NA />}</TableCell>
                    <TableCell>{row.settlement_date ? DateFormatter(row.settlement_date) : <NA />}</TableCell>
                    <TableCell>{row.comment || <NA />} </TableCell>
                    <TableCell>{row.period_billed ? DateFormatter(row.period_billed) : <NA />}</TableCell>
                    <TableCell
                      align='right'>
                      <Typography variant='body2' fontWeight={600}>
                        {handleShowAmount(row.fee_currency, row.fee_billed || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align='right'>
                      <Chip
                        size='small'
                        color={getStatusChipColor(row.fee_status)}
                        label={row.fee_status} 
                        />
                    </TableCell>
                  </TableRow>)
                  : statusFilter === "ADDONS"
                    ?
                    (<></>)
                    : statusFilter === "DOM_PAY"
                      ? 
                      (<TableRow 
                        key={index}>
                        <TableCell>
                          <Typography
                            variant='body2'>
                            {row?.ref_payment_id?.transaction_id || <NA />}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.billing_date ? DateFormatter(row.billing_date) : <NA />}</TableCell>
                        <TableCell>{row?.ref_payment_id?.debit_date ? DateFormatter(row?.ref_payment_id?.debit_date) : <NA />}</TableCell>
                        <TableCell>{row.settlement_date ? DateFormatter(row.settlement_date) : <NA />}</TableCell>
                        <TableCell>
                          {
                            row?.ref_payment_id?.ref_vendor_id
                            ?
                            <Box
                              className="flexCenter_Row"
                              gap={"10px"}>
                              <NameAvatar
                                color={row?.ref_payment_id?.ref_vendor_id?.color || null}>
                                {row?.ref_payment_id?.ref_vendor_id?.name || "NA"}
                              </NameAvatar>
                              <Typography>
                                {row?.ref_payment_id?.ref_vendor_id?.name || "NA"}
                              </Typography>
                            </Box>
                            :
                            <NA label={"No supplier found"}/>
                          }
                        </TableCell>
                        <TableCell
                          align='right'>
                          <Typography variant='body2' fontWeight={600}>
                            {handleShowAmount(row?.ref_payment_id?.currency, row?.ref_payment_id?.amount || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align='right'>
                          <Typography variant='body2' fontWeight={600}>
                            {handleShowAmount(row.fee_currency, row.fee_billed || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align='right'>
                          <Chip
                            size='small'
                            color={getStatusChipColor(row.fee_status)}
                            label={row.fee_status} 
                            />
                        </TableCell>
                      </TableRow>
                      )
                      : statusFilter === "DOM_RCV"
                        ?
                        (<TableRow 
                          key={index}>
                          <TableCell>
                            <Typography
                              variant='body2'>
                              {row?.ref_receipt_id?.transaction_id || <NA />}
                            </Typography>
                          </TableCell>
                          <TableCell>{row.billing_date ? DateFormatter(row.billing_date) : <NA />}</TableCell>
                          <TableCell>{row.settlement_date ? DateFormatter(row.settlement_date) : <NA />}</TableCell>
                          <TableCell>
                            {row?.ref_receipt_id?.ref_invoice_id?.ref_customer_id?.name || <NA />}
                          </TableCell>
                          <TableCell>
                            {row?.ref_receipt_id?.ref_invoice_id?.invoice_number 
                              ? "#"+row?.ref_receipt_id?.ref_invoice_id?.invoice_number 
                              : <NA />}
                          </TableCell>
                          <TableCell
                            align='right'>
                            <Typography variant='body2' fontWeight={600}>
                              {handleShowAmount(row?.ref_receipt_id?.currency, row?.ref_receipt_id?.amount || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align='right'>
                            <Typography variant='body2' fontWeight={600}>
                              {handleShowAmount(row.fee_currency, row.fee_billed || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align='right'>
                            <Chip
                              size='small'
                              color={getStatusChipColor(row.fee_status)}
                              label={row.fee_status} 
                              />
                          </TableCell>
                        </TableRow>
                        )
                        : statusFilter === "DOM_WALLET_DEPOSIT"
                          ?
                          (<TableRow 
                            key={index}>
                            <TableCell>
                              <Typography
                                variant='body2'>
                                {row?.ref_deposit_id?.transaction_id || <NA />}
                              </Typography>
                            </TableCell>
                            <TableCell>{row.billing_date ? DateFormatter(row.billing_date) : <NA />}</TableCell>
                            <TableCell>{row.settlement_date ? DateFormatter(row.settlement_date) : <NA />}</TableCell>
                            <TableCell>
                              {`Deposit to x${row?.ref_deposit_id?.ref_co_bank_acc_id?.account_number}` || <NA />}
                            </TableCell>
                            <TableCell
                              align='right'>
                              <Typography variant='body2' fontWeight={600}>
                                {handleShowAmount(row.fee_currency, row.fee_billed || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell
                              align='right'>
                              <Chip
                                size='small'
                                color={getStatusChipColor(row.fee_status)}
                                label={row.fee_status} 
                                />
                            </TableCell>
                          </TableRow>
                          )
                          : statusFilter === "DOM_WALLET_WITHDRAWAL"
                            ?
                            (<TableRow 
                              key={index}>
                              <TableCell>
                                <Typography
                                  variant='body2'>
                                  {row?.ref_withdrawal_id?.transaction_id || <NA />}
                                </Typography>
                              </TableCell>
                              <TableCell>{row.billing_date ? DateFormatter(row.billing_date) : <NA />}</TableCell>
                              <TableCell>{row.settlement_date ? DateFormatter(row.settlement_date) : <NA />}</TableCell>
                              <TableCell>
                                {`Withdrawal to x${row?.ref_withdrawal_id?.ref_co_bank_acc_id?.account_number}` || <NA />}
                              </TableCell>
                              <TableCell
                                align='right'>
                                <Typography variant='body2' fontWeight={600}>
                                  {handleShowAmount(row.fee_currency, row.fee_billed || 0)}
                                </Typography>
                              </TableCell>
                              <TableCell
                                align='right'>
                                <Chip
                                  size='small'
                                  color={getStatusChipColor(row.fee_status)}
                                  label={row.fee_status} 
                                  />
                              </TableCell>
                            </TableRow>
                          )
                          : statusFilter === "INT_PAY"
                            ?
                            (<TableRow 
                              key={index}>
                              <TableCell>
                                <Typography
                                  variant='body2'>
                                  {row.transaction_id || <NA />}
                                </Typography>
                              </TableCell>
                              <TableCell>{row.billing_date ? DateFormatter(row.billing_date) : <NA />}</TableCell>
                              <TableCell>{row?.ref_payment_id?.debit_date ? DateFormatter(row?.ref_payment_id?.debit_date) : <NA />}</TableCell>
                              <TableCell>{row.settlement_date ? DateFormatter(row.settlement_date) : <NA />}</TableCell>
                              <TableCell>
                                {
                                  row?.ref_payment_id?.ref_vendor_id
                                  ?
                                  <Box
                                    className="flexCenter_Row"
                                    gap={"10px"}>
                                    <NameAvatar
                                      color={row?.ref_payment_id?.ref_vendor_id?.color || null}>
                                      {row?.ref_payment_id?.ref_vendor_id?.name || "NA"}
                                    </NameAvatar>
                                    <Typography>
                                      {row?.ref_payment_id?.ref_vendor_id?.name || "NA"}
                                    </Typography>
                                  </Box>
                                  :
                                  <NA label={"No supplier found"}/>
                                }
                              </TableCell>
                              <TableCell
                                align='right'>
                                <Typography variant='body2' fontWeight={600}>
                                  {handleShowAmount(row?.ref_payment_id?.currency, row?.ref_payment_id?.amount || 0)}
                                </Typography>
                              </TableCell>
                              <TableCell
                                align='right'>
                                <Typography variant='body2' fontWeight={600}>
                                  {handleShowAmount(row.fee_currency, row.fee_billed || 0)}
                                </Typography>
                              </TableCell>
                              <TableCell
                                align='right'>
                                <Chip
                                  size='small'
                                  color={getStatusChipColor(row.fee_status)}
                                  label={row.fee_status} 
                                  />
                              </TableCell>
                            </TableRow>
                            )
                            : statusFilter === "INT_RCV"
                              ?
                              (<TableRow 
                                key={index}>
                                <TableCell>
                                  <Typography
                                    variant='body2'>
                                    {row.transaction_id || <NA />}
                                  </Typography>
                                </TableCell>
                                <TableCell>{row.billing_date ? DateFormatter(row.billing_date) : <NA />}</TableCell>
                                <TableCell>{row.settlement_date ? DateFormatter(row.settlement_date) : <NA />}</TableCell>
                                <TableCell>
                                  {row?.ref_receipt_id?.ref_invoice_id?.ref_customer_id?.name || <NA />}
                                </TableCell>
                                <TableCell>
                                  {row?.ref_receipt_id?.ref_invoice_id?.invoice_number 
                                    ? "#"+row?.ref_receipt_id?.ref_invoice_id?.invoice_number 
                                    : <NA />}
                                </TableCell>
                                <TableCell
                                  align='right'>
                                  <Typography variant='body2' fontWeight={600}>
                                    {handleShowAmount(row?.ref_receipt_id?.currency, row?.ref_receipt_id?.amount || 0)}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align='right'>
                                  <Typography variant='body2' fontWeight={600}>
                                    {handleShowAmount(row.fee_currency, row.fee_billed || 0)}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align='right'>
                                  <Chip
                                    size='small'
                                    color={getStatusChipColor(row.fee_status)}
                                    label={row.fee_status} 
                                    />
                                </TableCell>
                              </TableRow>
                              )
                              : statusFilter === "INT_WALLET"
                                ?
                                (<TableRow 
                                  key={index}>
                                  <TableCell>
                                    <Typography
                                      variant='body2'>
                                      {row.transaction_id || <NA />}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{row.billing_date ? DateFormatter(row.billing_date) : <NA />}</TableCell>
                                  <TableCell>{row?.ref_payment_id?.debit_date ? DateFormatter(row?.ref_payment_id?.debit_date) : <NA />}</TableCell>
                                  <TableCell>{row.settlement_date ? DateFormatter(row.settlement_date) : <NA />}</TableCell>
                                  {/* <TableCell>
                                    {
                                      row?.ref_payment_id?.ref_vendor_id
                                      ?
                                      <Box
                                        className="flexCenter_Row"
                                        gap={"10px"}>
                                        <NameAvatar
                                          color={row?.ref_payment_id?.ref_vendor_id?.color || null}>
                                          {row?.ref_payment_id?.ref_vendor_id?.name || "NA"}
                                        </NameAvatar>
                                        <Typography>
                                          {row?.ref_payment_id?.ref_vendor_id?.name || "NA"}
                                        </Typography>
                                      </Box>
                                      :
                                      <NA label={"No supplier found"}/>
                                    }
                                  </TableCell>
                                  <TableCell
                                    align='right'>
                                    <Typography variant='body2' fontWeight={600}>
                                      {handleShowAmount(row?.ref_payment_id?.currency, row?.ref_payment_id?.amount || 0)}
                                    </Typography>
                                  </TableCell> */}
                                  <TableCell
                                    align='right'>
                                    <Typography variant='body2' fontWeight={600}>
                                      {handleShowAmount(row.fee_currency, row.fee_billed || 0)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell
                                    align='right'>
                                    <Chip
                                      size='small'
                                      color={getStatusChipColor(row.fee_status)}
                                      label={row.fee_status} 
                                      />
                                  </TableCell>
                                </TableRow>
                                )
                                :
                                null
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={billingTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
    </Box>
  )
}

export default BillingPage;